import { useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";
import {
  EMPTY_CONTEXT,
  useBaseBooleanFeatureFlag,
  useBaseVariantFeatureFlag,
} from "@bto/shared";
import { BatchedSetAnalyticsUser } from "../../services/Analytics";
import {
  getSanitizedProviderData,
  getDetailedProviderData,
} from "../contextUtils";
import { providerProfileState, providerState } from "../../recoil/atoms";

/**
 * React hook to check if a boolean feature flag is enabled for the current provider session.
 *
 * Resolves provider identity from Recoil state and delegates LaunchDarkly evaluation
 * to the shared {@link useBaseBooleanFeatureFlag} hook.
 *
 * When no `additionalContext` is provided, the hook automatically includes the
 * provider's profile data (e.g. lims_id) so that flag targeting rules based on
 * those attributes evaluate correctly via an explicit `identify` call.
 * Pass `EMPTY_CONTEXT` explicitly to opt out of this behaviour.
 *
 * @param flagKey The LaunchDarkly flag key to evaluate
 * @param additionalContext Extra context merged in for this specific evaluation (triggers identify).
 *   Omit to auto-include provider profile data; pass EMPTY_CONTEXT to skip.
 * @param entityIdInput Optional entity ID override; defaults to the provider's username
 */
export const useBooleanFeatureFlag = (
  flagKey: string,
  additionalContext?: Record<string, unknown>,
  entityIdInput?: string,
): { enabled: boolean; loading: boolean } => {
  const providerInfo = useRecoilValue(providerState);
  const providerProfile = useRecoilValue(providerProfileState);

  const entityId = entityIdInput ?? providerInfo?.username;

  const userContext = useMemo(() => {
    if (!providerInfo) return EMPTY_CONTEXT;
    const providerData = getSanitizedProviderData(providerInfo);
    if (!providerProfile) return providerData;
    return {
      ...providerData,
      ...getDetailedProviderData(providerProfile),
    };
  }, [providerInfo, providerProfile]);

  const resolvedContext = useMemo(
    () =>
      additionalContext ??
      (providerProfile
        ? getDetailedProviderData(providerProfile)
        : EMPTY_CONTEXT),
    [additionalContext, providerProfile],
  );

  // Flush any pending batched analytics on unmount so in-flight flag
  // evaluations are not silently dropped during SPA navigation or page unload.
  useEffect(() => () => BatchedSetAnalyticsUser.flush(), []);

  return useBaseBooleanFeatureFlag(
    flagKey,
    entityId,
    userContext,
    resolvedContext,
    (enabled) => {
      if (providerInfo && entityId) {
        BatchedSetAnalyticsUser(providerInfo, providerProfile, entityId, {
          [flagKey]: enabled.toString(),
        });
      }
    },
  );
};

/**
 * React hook to check a variant (string-valued) feature flag for the current provider session.
 *
 * Resolves provider identity from Recoil state and delegates LaunchDarkly evaluation
 * to the shared {@link useBaseVariantFeatureFlag} hook.
 *
 * When no `additionalContext` is provided, the hook automatically includes the
 * provider's profile data (e.g. lims_id) so that flag targeting rules based on
 * those attributes evaluate correctly via an explicit `identify` call.
 * Pass `EMPTY_CONTEXT` explicitly to opt out of this behaviour.
 *
 * @param flagKey The LaunchDarkly flag key to evaluate
 * @param additionalContext Extra context merged in for this specific evaluation (triggers identify).
 *   Omit to auto-include provider profile data; pass EMPTY_CONTEXT to skip.
 * @param entityIdInput Optional entity ID override; defaults to the provider's username
 */
export const useVariantFeatureFlag = (
  flagKey: string,
  additionalContext?: Record<string, unknown>,
  entityIdInput?: string,
): { variant: string; loading: boolean } => {
  const providerInfo = useRecoilValue(providerState);
  const providerProfile = useRecoilValue(providerProfileState);

  const entityId = entityIdInput ?? providerInfo?.username;

  const userContext = useMemo(() => {
    if (!providerInfo) return EMPTY_CONTEXT;
    const providerData = getSanitizedProviderData(providerInfo);
    if (!providerProfile) return providerData;
    return {
      ...providerData,
      ...getDetailedProviderData(providerProfile),
    };
  }, [providerInfo, providerProfile]);

  const resolvedContext = useMemo(
    () =>
      additionalContext ??
      (providerProfile
        ? getDetailedProviderData(providerProfile)
        : EMPTY_CONTEXT),
    [additionalContext, providerProfile],
  );

  useEffect(() => () => BatchedSetAnalyticsUser.flush(), []);

  return useBaseVariantFeatureFlag(
    flagKey,
    entityId,
    userContext,
    resolvedContext,
    (variant) => {
      if (providerInfo && entityId) {
        BatchedSetAnalyticsUser(providerInfo, providerProfile, entityId, {
          [flagKey]: variant,
        });
      }
    },
  );
};
